import React, { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import moment from "moment"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"
import ChartInfo from "./ChartInfo"

const INITIAL_FILTERS = { product_id: "", date_from: null, date_to: null }

function DeviceUsage() {
  const [loading, setLoading] = useState(true)
  const [labels, setLabels] = useState([])
  const [historical, setHistorical] = useState([])
  const [predicted, setPredicted] = useState([])
  const [products, setProducts] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function loadData() {
    setLoading(true)

    const f = `${filters.date_from ? `&date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`

    Api.get(
      `analytics/appliance-usage-prediction?product_id=${filters.product_id}${f}`,
    )
      .then((res) => {
        setLabels(
          res.data.chart_data.labels?.map((date) => {
            return moment(date, "YYYY-MM").isSame(moment(), "month")
              ? trans("analytics.today")
              : moment(date, "YYYY-MM").format("MM.YYYY")
          }) || [],
        )
        setHistorical(res.data.chart_data.usage_counts_historical || [])
        setPredicted(res.data.chart_data.usage_counts_predicted || [])
        setProducts(res.data.products || [])

        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  const historicalSeries = labels.map((_, i) =>
    i < historical.length ? (historical[i] ?? null) : null,
  )

  const boundaryIndex = historical.length - 1
  const predictedSeries = labels.map((_, i) => {
    if (i === boundaryIndex) return historical[boundaryIndex] ?? null
    if (i > boundaryIndex) return predicted[i - historical.length] ?? null
    return null
  })

  function hasAnyData() {
    return labels.length > 0
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.product_id) count++
    if (filters.date_from || filters.date_to) count++
    return count
  }

  return (
    <div className="repair-requests-chart">
      <div className="head">
        <h3>
          {trans("analytics.deviceUsage")}
          <ChartInfo name="applianceUsagePrediction" />
        </h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                  monthOnly
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  value={filters.product_id || ""}
                  name="product_id"
                  onChange={(e) =>
                    handleFilterChange("product_id", e.target.value)
                  }
                >
                  <option value="">{trans("random.all")}</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : !hasAnyData() ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <LineChart
          xAxis={[{ scaleType: "point", data: labels }]}
          series={[
            {
              id: "du-hist",
              curve: "linear",
              label: trans("analytics.historicalData"),
              area: true,
              data: historicalSeries,
              color: "#181F67",
              valueFormatter: (v) => (v == null ? "" : String(v)),
            },
            {
              id: "du-pred",
              curve: "linear",
              label: trans("analytics.forecast"),
              data: predictedSeries,
              color: "#181F67",
              valueFormatter: (v) => (v == null ? "" : String(v)),
            },
          ]}
          height={295}
          sx={{
            "& .MuiLineElement-series-du-pred": { strokeDasharray: "6 3" },
            "& .MuiAreaElement-series-du-hist": {
              fill: "url(#du-hist-gradient)",
            },
            "& .MuiLineElement-root": { strokeWidth: 1 },
          }}
          slotProps={{
            legend: { hidden: true },
          }}
        >
          <defs>
            <linearGradient id="du-hist-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#181F67" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#181F67" stopOpacity={0.02} />
            </linearGradient>
          </defs>
        </LineChart>
      )}
    </div>
  )
}

export default DeviceUsage
